import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, MessageCircle, Calendar as Cal, Send, Check, X, Pencil, FileText, Loader2, Sparkles, ArrowUpRight, CalendarPlus, UserPlus, NotebookPen } from "lucide-react";
import { api } from "@/lib/api";
import { outlookMailCompose, openUrl } from "@/lib/outlook";
import { toast } from "sonner";

const KIND_LABEL = {
  email: { Icon: Mail, label: "Email draft" },
  post: { Icon: Send, label: "Post draft" },
  message: { Icon: MessageCircle, label: "Message draft" },
  calendar: { Icon: Cal, label: "Calendar draft" },
  document: { Icon: FileText, label: "Document draft" },
  person: { Icon: UserPlus, label: "Contact capture" },
  vault_note: { Icon: NotebookPen, label: "Vault note" },
};

// Module-level registry: voice "approve" / "skip" target the MOST RECENT
// pending capture card (person / vault_note). Each card registers itself
// while pending and unregisters on approve / skip / unmount. This lets the
// VoiceOverlay say "yes, save it" → approve without any tap.
//
// Race protection: if the user says "save it" BEFORE the DraftCard has
// finished mounting (slow mobile network, conversation still loading), we
// hold the pending intent here for up to 5s. The next capture card to
// register will pick it up — so voice approval is reliable even when a
// turn round-trip is racing the chat-page hydration.
const _voiceTargets = []; // [{ id, kind, approve, skip }]
let _pendingVoiceAction = null; // { kind: 'approve'|'skip', expires: ms }
const _PENDING_TTL_MS = 5000;

function _registerVoiceTarget(t) {
  _voiceTargets.push(t);
  if (_pendingVoiceAction && Date.now() < _pendingVoiceAction.expires) {
    const action = _pendingVoiceAction.kind;
    _pendingVoiceAction = null;
    // Defer one tick so React finishes mounting the card before we call into it.
    setTimeout(() => { (action === "skip" ? t.skip : t.approve)(); }, 0);
  }
}
function _unregisterVoiceTarget(id) {
  const idx = _voiceTargets.findIndex((t) => t.id === id);
  if (idx >= 0) _voiceTargets.splice(idx, 1);
}
if (typeof window !== "undefined" && !window.__phVoiceWired) {
  window.__phVoiceWired = true;
  window.addEventListener("ph:voice-approve-latest", () => {
    const t = _voiceTargets[_voiceTargets.length - 1];
    if (t) t.approve();
    else _pendingVoiceAction = { kind: "approve", expires: Date.now() + _PENDING_TTL_MS };
  });
  window.addEventListener("ph:voice-skip-latest", () => {
    const t = _voiceTargets[_voiceTargets.length - 1];
    if (t) t.skip();
    else _pendingVoiceAction = { kind: "skip", expires: Date.now() + _PENDING_TTL_MS };
  });
}

export default function DraftCard({ conversationId, draft, onUpdate }) {
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [fields, setFields] = useState(draft.fields || {});
  const [status, setStatus] = useState(draft.status || "pending");
  const [generating, setGenerating] = useState(false);
  const [outlookLink, setOutlookLink] = useState(draft.outlook_web_link || null);
  const meta = KIND_LABEL[draft.kind] || KIND_LABEL.email;
  const Icon = meta.Icon;
  const isDocument = draft.kind === "document";
  const isCalendar = draft.kind === "calendar";
  const isEmail = draft.kind === "email";
  const isPerson = draft.kind === "person";
  const isVaultNote = draft.kind === "vault_note";
  // Latest values used by the voice-approve registry below.
  const personRef = useRef(null);
  const vaultRef = useRef(null);
  const skipRef = useRef(null);

  const toList = (v) => {
    if (!v) return [];
    if (Array.isArray(v)) return v.filter(Boolean);
    return String(v).split(/[;,]/).map((s) => s.trim()).filter(Boolean);
  };

  const escapeHtml = (s) =>
    String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const bodyToHtml = (s) => escapeHtml(s).replace(/\n/g, "<br>");

  // Primary "Approve" action for email drafts: save a real draft into the
  // user's Outlook Drafts folder via Graph. Never sends. Falls back to an
  // Outlook compose deeplink if Microsoft 365 isn't connected.
  const saveToOutlookDrafts = async () => {
    if (generating) return;
    setGenerating(true);
    try {
      const r = await api.post("/integrations/microsoft/drafts", {
        subject: fields.subject || draft.title || "(no subject)",
        body_html: bodyToHtml(fields.body || ""),
        to: toList(fields.to),
        cc: toList(fields.cc),
      });
      setOutlookLink(r.data?.web_link || null);
      await update("approved", null, true);
      toast.success("Saved to your Outlook Drafts — review and send it there.");
    } catch (e) {
      const code = e?.response?.status;
      if (code === 400) {
        // Microsoft 365 not connected — fall back to the Outlook compose deeplink.
        openUrl(outlookMailCompose({ to: fields.to, cc: fields.cc, subject: fields.subject, body: fields.body }));
        await update("approved", null, true);
        toast.message("Opened Outlook compose — connect Microsoft 365 to save drafts automatically.");
      } else {
        // Transient/Graph error — keep the card pending so the user can retry.
        toast.error("Couldn't save the draft to Outlook just now. Please try again.");
      }
    } finally {
      setGenerating(false);
    }
  };

  const openSavedDraft = () => {
    if (outlookLink) openUrl(outlookLink);
    else openUrl(outlookMailCompose({ to: fields.to, cc: fields.cc, subject: fields.subject, body: fields.body }));
  };

  const addToMeetings = async () => {
    if (generating) return;
    setGenerating(true);
    try {
      await api.post("/calendar/pending", {
        title: draft.title || fields.subject || fields.title || "Meeting",
        start: fields.start || fields.startdt || null,
        end: fields.end || fields.enddt || null,
        when_text: fields.when || fields.time || fields.date || "",
        attendees: toList(fields.attendees || fields.to || fields.with),
        location: fields.location || "",
        notes: fields.body || fields.notes || fields.agenda || "",
      });
      await update("approved");
      toast.success("Added to Meetings — approve it in Outlook when ready.");
    } catch {
      toast.error("Couldn't add the invite to Meetings.");
    } finally {
      setGenerating(false);
    }
  };

  const savePerson = async () => {
    if (generating) return;
    setGenerating(true);
    try {
      const groups = toList(fields.groups);
      const r = await api.post("/people/upsert", {
        name: fields.name || draft.title,
        role: fields.role || null,
        relationship: fields.relationship || null,
        groups: groups.length ? groups : null,
        birthday: fields.birthday || null,
        location: fields.location || null,
        address: fields.address || null,
        email: fields.email || null,
        phone: fields.phone || null,
        notes: fields.notes || null,
      });
      await update("approved", null, true);
      const created = r.data?.created;
      const name = r.data?.person?.name || fields.name;
      toast.success(created ? `Saved ${name} to People.` : `Updated ${name} in People.`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't save the contact.";
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  };

  const saveVaultNote = async () => {
    if (generating) return;
    setGenerating(true);
    try {
      const tags = toList(fields.tags);
      const folder = (fields.folder || "00 Inbox").trim();
      await api.post("/vault/docs", {
        // user_id is overwritten server-side from the token
        user_id: "_",
        folder,
        name: fields.name || draft.title || "Captured note",
        markdown: fields.content || fields.markdown || "",
        tags,
      });
      await update("approved", null, true);
      toast.success(`Saved to Vault → ${folder}.`);
    } catch {
      toast.error("Couldn't save the note.");
    } finally {
      setGenerating(false);
    }
  };

  const generateDoc = async () => {
    if (generating) return;
    setGenerating(true);
    try {
      const brief = fields.brief || draft.title || "";
      const fmt = fields.format === "deck" ? "deck" : "document";
      const r = await api.post("/studio/generate", { prompt: brief, kind: fmt });
      setStatus("generated");
      onUpdate && onUpdate(draft.id, "generated");
      toast.success("Draft created in Studio.");
      navigate(`/studio?artifact=${r.data.id}`);
    } catch {
      toast.error("Could not generate the document.");
    } finally {
      setGenerating(false);
    }
  };

  const update = async (newStatus, newFields = null, silent = false) => {
    try {
      await api.post(`/conversations/${conversationId}/drafts/${draft.id}`, {
        status: newStatus,
        fields: newFields,
      });
      setStatus(newStatus);
      if (newFields) setFields(newFields);
      onUpdate && onUpdate(draft.id, newStatus, newFields);
      if (!silent) {
        if (newStatus === "approved") toast.success("Confirmed. Nothing was sent — v1 is drafts only.");
        if (newStatus === "skipped") toast("Skipped.");
        if (newStatus === "edited") toast.success("Edits saved.");
      }
    } catch (e) {
      toast.error("Could not update draft.");
    }
  };

  // Keep refs fresh so the voice-approve registry always calls the latest
  // handlers (closures over up-to-date fields). Done in a post-commit effect
  // so we don't mutate refs during render.
  useEffect(() => {
    personRef.current = savePerson;
    vaultRef.current = saveVaultNote;
    skipRef.current = () => update("skipped");
  });

  // Register this card as the latest voice-approval target while pending +
  // capture-kind. Cleared when the card leaves pending or unmounts.
  useEffect(() => {
    if ((isPerson || isVaultNote) && status === "pending" && !editing) {
      const t = {
        id: draft.id,
        kind: draft.kind,
        approve: () => (isPerson ? personRef.current?.() : vaultRef.current?.()),
        skip: () => skipRef.current?.(),
      };
      _registerVoiceTarget(t);
      return () => _unregisterVoiceTarget(draft.id);
    }
    return undefined;
  }, [isPerson, isVaultNote, status, editing, draft.id, draft.kind]);

  return (
    <div className="draft-card" data-testid={`draft-card-${draft.id}`}>
      <div className="draft-head">
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Icon size={16} className="text-cobalt" strokeWidth={1.7} />
          <div>
            <div className="draft-kind">{meta.label}</div>
            <div className="draft-title">{draft.title}</div>
          </div>
        </div>
        {status !== "pending" && (
          <span className={`draft-status-pill status-${status}`} data-testid={`draft-status-${draft.id}`}>
            {status}
          </span>
        )}
      </div>

      {editing ? (
        <div style={{ padding: "12px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
          {Object.entries(fields).map(([k, v]) => (
            <div key={k}>
              <div className="text-dim" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 4 }}>{k}</div>
              {String(v).length > 60 || k === "body" ? (
                <textarea
                  className="field"
                  value={String(v)}
                  onChange={(e) => setFields({ ...fields, [k]: e.target.value })}
                  rows={k === "body" ? 6 : 2}
                  data-testid={`draft-field-${k}`}
                />
              ) : (
                <input
                  className="field"
                  value={String(v)}
                  onChange={(e) => setFields({ ...fields, [k]: e.target.value })}
                  data-testid={`draft-field-${k}`}
                />
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="draft-body">
          {isDocument ? (
            <>
              <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                {fields.doc_kind && <span className="props-chip">{fields.doc_kind}</span>}
                <span className="props-chip">{fields.format === "deck" ? "presentation" : "document"}</span>
              </div>
              <div>{fields.brief || draft.title}</div>
            </>
          ) : isPerson ? (
            <>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                {fields.relationship && <span className="props-chip">{fields.relationship}</span>}
                {toList(fields.groups).map((g) => (
                  <span key={g} className="props-chip">{g}</span>
                ))}
              </div>
              {fields.name && <div><span className="text-dim">Name: </span>{fields.name}</div>}
              {fields.birthday && <div><span className="text-dim">Birthday: </span>{fields.birthday}</div>}
              {fields.location && <div><span className="text-dim">Location: </span>{fields.location}</div>}
              {fields.email && <div><span className="text-dim">Email: </span>{fields.email}</div>}
              {fields.phone && <div><span className="text-dim">Phone: </span>{fields.phone}</div>}
              {fields.address && <div style={{ marginTop: 4 }}><span className="text-dim">Address: </span>{fields.address}</div>}
              {fields.notes && <div style={{ marginTop: 8, whiteSpace: "pre-wrap" }}>{fields.notes}</div>}
            </>
          ) : isVaultNote ? (
            <>
              <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                <span className="props-chip">{fields.folder || "00 Inbox"}</span>
                {toList(fields.tags).map((t) => (
                  <span key={t} className="props-chip">#{t}</span>
                ))}
              </div>
              <div style={{ whiteSpace: "pre-wrap" }}>{fields.content || fields.markdown}</div>
            </>
          ) : (
            <>
              {fields.to && <div><span className="text-dim">To: </span>{fields.to}</div>}
              {fields.subject && <div><span className="text-dim">Subject: </span>{fields.subject}</div>}
              {fields.body && <div style={{ marginTop: 8 }}>{fields.body}</div>}
              {!fields.body && !fields.subject && (
                <pre style={{ margin: 0, fontFamily: "inherit", whiteSpace: "pre-wrap" }}>{JSON.stringify(fields, null, 2)}</pre>
              )}
            </>
          )}
        </div>
      )}

      <div className="draft-foot">
        {isDocument && status === "pending" && !editing && (
          <>
            <button className="btn btn-ghost" onClick={() => update("skipped")} data-testid={`draft-skip-${draft.id}`}>
              <X size={14} /> Dismiss
            </button>
            <button className="btn btn-primary" onClick={generateDoc} disabled={generating} data-testid={`draft-generate-${draft.id}`}>
              {generating ? <Loader2 size={14} className="spin" /> : <Sparkles size={14} />} Generate in Studio
            </button>
          </>
        )}
        {!isDocument && !isCalendar && !isEmail && !isPerson && !isVaultNote && status === "pending" && !editing && (
          <>
            <button className="btn btn-ghost" onClick={() => update("skipped")} data-testid={`draft-skip-${draft.id}`}>
              <X size={14} /> Skip
            </button>
            <button className="btn" onClick={() => setEditing(true)} data-testid={`draft-edit-${draft.id}`}>
              <Pencil size={14} /> Edit
            </button>
            <button className="btn btn-primary" onClick={() => update("approved")} data-testid={`draft-approve-${draft.id}`}>
              <Check size={14} /> Approve
            </button>
          </>
        )}
        {isPerson && status === "pending" && !editing && (
          <>
            <button className="btn btn-ghost" onClick={() => update("skipped")} data-testid={`draft-skip-${draft.id}`}>
              <X size={14} /> Skip
            </button>
            <button className="btn" onClick={() => setEditing(true)} data-testid={`draft-edit-${draft.id}`}>
              <Pencil size={14} /> Edit
            </button>
            <button className="btn btn-primary" onClick={savePerson} disabled={generating} data-testid={`draft-approve-${draft.id}`}>
              {generating ? <Loader2 size={14} className="spin" /> : <UserPlus size={14} />} Save to People
            </button>
          </>
        )}
        {isVaultNote && status === "pending" && !editing && (
          <>
            <button className="btn btn-ghost" onClick={() => update("skipped")} data-testid={`draft-skip-${draft.id}`}>
              <X size={14} /> Skip
            </button>
            <button className="btn" onClick={() => setEditing(true)} data-testid={`draft-edit-${draft.id}`}>
              <Pencil size={14} /> Edit
            </button>
            <button className="btn btn-primary" onClick={saveVaultNote} disabled={generating} data-testid={`draft-approve-${draft.id}`}>
              {generating ? <Loader2 size={14} className="spin" /> : <NotebookPen size={14} />} Save to Vault
            </button>
          </>
        )}
        {isEmail && (status === "pending" || status === "edited") && !editing && (
          <>
            <button className="btn btn-ghost" onClick={() => update("skipped")} data-testid={`draft-skip-${draft.id}`}>
              <X size={14} /> Skip
            </button>
            <button className="btn" onClick={() => setEditing(true)} data-testid={`draft-edit-${draft.id}`}>
              <Pencil size={14} /> Edit
            </button>
            <button className="btn btn-primary" onClick={saveToOutlookDrafts} disabled={generating} data-testid={`draft-approve-${draft.id}`}>
              {generating ? <Loader2 size={14} className="spin" /> : <Check size={14} />} Approve &amp; save to Outlook
            </button>
          </>
        )}
        {isCalendar && status === "pending" && !editing && (
          <>
            <button className="btn btn-ghost" onClick={() => update("skipped")} data-testid={`draft-skip-${draft.id}`}>
              <X size={14} /> Skip
            </button>
            <button className="btn" onClick={() => setEditing(true)} data-testid={`draft-edit-${draft.id}`}>
              <Pencil size={14} /> Edit
            </button>
            <button className="btn btn-primary" onClick={addToMeetings} disabled={generating} data-testid={`draft-add-meeting-${draft.id}`}>
              {generating ? <Loader2 size={14} className="spin" /> : <CalendarPlus size={14} />} Add to Meetings
            </button>
          </>
        )}
        {isEmail && status === "approved" && !editing && (
          <button className="btn" onClick={openSavedDraft} data-testid={`draft-open-outlook-${draft.id}`}>
            <ArrowUpRight size={14} /> Open in Outlook
          </button>
        )}
        {editing && (
          <>
            <button className="btn btn-ghost" onClick={() => { setFields(draft.fields || {}); setEditing(false); }}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={() => { update("edited", fields); setEditing(false); }} data-testid={`draft-save-${draft.id}`}>
              <Check size={14} /> Save edits
            </button>
          </>
        )}
      </div>
    </div>
  );
}
